import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'

export const PRODUCT_SHARED_EVIDENCE_QUERY_KEY =
  'productSharedEvidence' as const

export type ProductSharedEvidenceSlot =
  | 'PRODUCT_IDENTIFICATION'
  | 'MANUFACTURER_INFORMATION'

export type ProductSharedEvidenceSlotView = {
  slotKind: ProductSharedEvidenceSlot
  fileName: string | null
  contentType: string | null
  fileSizeKb: number | null
  uploadedAt: string | null
  uploadedByUserId: string | null
}

export type ProductSharedEvidenceView = {
  slots: ProductSharedEvidenceSlotView[]
}

export function useGetProductSharedEvidence(productId: string | undefined) {
  return useQuery<ProductSharedEvidenceView, Error>({
    queryKey: [PRODUCT_SHARED_EVIDENCE_QUERY_KEY, productId],
    queryFn: async () => {
      const response = await apiClient.get<ProductSharedEvidenceView>(
        `/v2/products/${productId}/shared-evidence`
      )
      return response.data
    },
    enabled: productId != null && productId.length > 0,
  })
}

export function useUploadProductSharedEvidenceSlot(productId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      slotKind,
      file,
    }: {
      slotKind: ProductSharedEvidenceSlot
      file: File
    }) => {
      const formData = new FormData()
      formData.append('file', file, file.name)
      formData.append('fileName', file.name)
      const response = await apiClient.post<ProductSharedEvidenceView>(
        `/v2/products/${productId}/shared-evidence/slots/${slotKind}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [PRODUCT_SHARED_EVIDENCE_QUERY_KEY, productId],
        data
      )
    },
  })
}

export function isProductSharedEvidenceSlotFilled(
  evidence: ProductSharedEvidenceView | undefined,
  slotKind: ProductSharedEvidenceSlot
): boolean {
  if (evidence == null) {
    return false
  }
  const slot = evidence.slots.find((entry) => entry.slotKind === slotKind)
  return slot?.fileName != null && slot.fileName.trim().length > 0
}
